import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GoogleSignInButton, useGoogleAuth } from '@questlabs/auth-js';
import * as FiIcons from 'react-icons/fi';
import { ncbData } from '../../api';
import SafeIcon from '../../common/SafeIcon';
import LIFE_INTERVIEW_SECTIONS from './lifeInterviewPrompts';

const { FiBookOpen, FiArrowLeft, FiArrowRight, FiCheck, FiHeart, FiLoader } = FiIcons;

const INITIAL_ANSWERS = LIFE_INTERVIEW_SECTIONS.flatMap((section) =>
  section.questions.map(() => '')
);

function InterviewProgress({ current, total }) {
  return (
    <div className="mb-8">
      <div className="mb-2 flex items-center justify-between text-xs font-bold text-text-muted">
        <span>Chapter {current + 1} of {total}</span>
        <span>{Math.round(((current + 1) / total) * 100)}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-brand-light">
        <div className="h-full rounded-full bg-accent-gold transition-all" style={{ width: `${((current + 1) / total) * 100}%` }} />
      </div>
    </div>
  );
}

function InterviewSaved({ onRestart }) {
  return (
    <div className="rounded-[32px] border border-green-200 bg-green-50 p-8 text-center md:p-12">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-700">
        <SafeIcon icon={FiCheck} className="h-7 w-7" />
      </div>
      <h2 className="mb-3 font-serif text-3xl text-accent-navy">Your story is safely saved</h2>
      <p className="mx-auto mb-7 max-w-xl text-sm leading-relaxed text-text-muted">
        Your interview is saved privately with your legacy documents. You can return to your account and continue adding chapters whenever you are ready.
      </p>
      <button onClick={onRestart} className="rounded-xl bg-accent-navy px-6 py-3 text-xs font-black uppercase tracking-widest text-white transition hover:bg-accent-gold">
        Start another interview
      </button>
    </div>
  );
}

export default function GuidedLifeInterviews() {
  const { user } = useGoogleAuth();
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [current, setCurrent] = useState(0);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const sections = useMemo(() => LIFE_INTERVIEW_SECTIONS, []);
  const answeredCount = answers.filter((answer) => answer.trim()).length;
  const section = sections[current];

  const updateAnswer = (questionIndex, value) => {
    const answerIndex = sections.slice(0, current).reduce((sum, item) => sum + item.questions.length, 0) + questionIndex;
    setAnswers((previous) => previous.map((answer, index) => index === answerIndex ? value : answer));
  };

  const submitInterview = async (event) => {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    setError('');
    const interviewData = sections.map((item, sectionIndex) => {
      const offset = sections.slice(0, sectionIndex).reduce((sum, entry) => sum + entry.questions.length, 0);
      return {
        chapter: item.title,
        responses: item.questions.map((question, index) => ({
          question,
          answer: answers[offset + index].trim(),
        })).filter((response) => response.answer),
      };
    }).filter((chapter) => chapter.responses.length);

    const now = new Date().toISOString();
    const result = await ncbData('create', 'will_documents_1748000000', {
      title: `Life interview${name.trim() ? ` — ${name.trim()}` : ''}`,
      status: 'interview',
      interview_data: JSON.stringify({ interviewee: name.trim(), chapters: interviewData }),
      created_at: now,
      updated_at: now,
    });
    setSaving(false);
    if (result?.status === 'success') setSaved(true);
    else setError(result?.error || 'We could not save your interview. Please try again.');
  };

  const restart = () => {
    setAnswers(INITIAL_ANSWERS);
    setCurrent(0);
    setName('');
    setError('');
    setSaved(false);
  };

  if (saved) return <main className="min-h-screen bg-brand-light px-4 pb-16 pt-28"><div className="mx-auto max-w-3xl"><InterviewSaved onRestart={restart} /></div></main>;

  return (
    <main className="min-h-screen bg-brand-light px-4 pb-16 pt-28">
      <div className="mx-auto max-w-4xl">
        <Link to="/resources" className="mb-8 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-accent-navy transition hover:text-accent-gold">
          <SafeIcon icon={FiArrowLeft} className="h-4 w-4" /> Resources
        </Link>
        <header className="mb-8 rounded-[32px] bg-accent-navy p-7 text-white md:p-10">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-accent-gold/30 bg-accent-gold/15 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-accent-gold">
            <SafeIcon icon={FiHeart} className="h-4 w-4" /> OneFinalLegacy
          </div>
          <h1 className="mb-4 font-serif text-4xl md:text-5xl">Guided Life Interviews</h1>
          <p className="max-w-2xl text-sm leading-relaxed text-white/75">
            Capture the moments, people, and lessons that shaped a life. Answer at your own pace; skip any prompt that does not feel right.
          </p>
          <p className="mt-5 text-xs text-white/55">Your answers are saved privately to your account only when you choose Save interview.</p>
        </header>

        {!user ? (
          <section className="rounded-[28px] border border-brand-dark bg-white p-8 text-center">
            <SafeIcon icon={FiBookOpen} className="mx-auto mb-4 h-9 w-9 text-accent-gold" />
            <h2 className="mb-2 font-serif text-2xl text-accent-navy">Sign in to save an interview</h2>
            <p className="mb-6 text-sm text-text-muted">Your interview will be stored privately with your legacy documents.</p>
            <GoogleSignInButton />
          </section>
        ) : (
          <form onSubmit={submitInterview} className="rounded-[32px] border border-brand-dark bg-white p-6 shadow-sm md:p-10">
            <InterviewProgress current={current} total={sections.length} />
            <div className="mb-7">
              <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-accent-gold">{section.title}</p>
              <h2 className="mb-2 font-serif text-2xl text-accent-navy">{section.title}</h2>
              <p className="text-sm text-text-muted">{section.introduction}</p>
            </div>

            {current === 0 && (
              <label className="mb-7 block">
                <span className="mb-2 block text-xs font-bold text-accent-navy">Whose story are we capturing? <span className="font-normal text-text-muted">(optional)</span></span>
                <input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} placeholder="Name or a short title" className="w-full rounded-xl border border-brand-dark px-4 py-3 text-sm outline-none transition focus:border-accent-gold" />
              </label>
            )}

            <div className="space-y-6">
              {section.questions.map((question, index) => {
                const offset = sections.slice(0, current).reduce((sum, item) => sum + item.questions.length, 0);
                return (
                  <label key={question} className="block">
                    <span className="mb-2 block text-sm font-bold leading-relaxed text-accent-navy">{question}</span>
                    <textarea value={answers[offset + index]} onChange={(event) => updateAnswer(index, event.target.value)} rows={4} maxLength={4000} placeholder="Write a few words, or tell the story in your own way…" className="w-full resize-y rounded-2xl border border-brand-dark bg-brand-light/40 px-4 py-3 text-sm leading-relaxed outline-none transition focus:border-accent-gold focus:bg-white" />
                  </label>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-brand-light pt-6 sm:flex-row sm:items-center sm:justify-between">
              <button type="button" disabled={current === 0} onClick={() => setCurrent((step) => Math.max(0, step - 1))} className="rounded-xl border border-brand-dark px-5 py-3 text-xs font-black uppercase tracking-widest text-accent-navy transition hover:border-accent-gold disabled:opacity-40">
                Previous chapter
              </button>
              {current < sections.length - 1 ? (
                <button type="button" onClick={() => setCurrent((step) => Math.min(sections.length - 1, step + 1))} className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent-navy px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition hover:bg-accent-gold">
                  Next chapter <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                </button>
              ) : (
                <button type="submit" disabled={saving || answeredCount === 0} className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent-gold px-6 py-3 text-xs font-black uppercase tracking-widest text-white transition hover:bg-accent-navy disabled:cursor-not-allowed disabled:opacity-50">
                  {saving ? <><SafeIcon icon={FiLoader} className="h-4 w-4 animate-spin" /> Saving…</> : <><SafeIcon icon={FiCheck} className="h-4 w-4" /> Save interview</>}
                </button>
              )}
            </div>
            <p className="mt-4 text-center text-[11px] text-text-muted">{answeredCount} prompt{answeredCount === 1 ? '' : 's'} answered</p>
            {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          </form>
        )}
      </div>
    </main>
  );
}